import type { ClaimsData } from '@/types';
import CarIcon from '@icons/car.svg?react';
import ExpenseIcon from '@icons/expense.svg?react';
import MedicIcon from '@icons/medic.svg?react';
import OtherEventIcon from '@icons/otherEvent.svg?react';
import ReviewIcon from '@icons/review.svg?react';
import MessageIcon from '@icons/tellUs.svg?react';
import TravelIcon from '@icons/travelTime.svg?react';
import LuggageIcon from '@icons/trolleyBag.svg?react';
import UploadDocIcon from '@icons/uploadDocument.svg?react';
import desktopBannerImage from '@images/MyClaims-herobanner-co-branded-desktop.png';
import mobileBannerImage from '@images/MyClaims-herobanner-co-branded-mobile.png';
import tabletBannerImage from '@images/MyClaims-herobanner-co-branded-tablet.png';
import { ROUTE_PATHS, ROUTES } from '../../../../config';

const claimsData: ClaimsData = {
  headerBanner: {
    id: 'my-claims-banner',
    title: 'My claims',
    description:
      'Start a claim and check the progress of active claims here. You can see your past claims here too.',
    backgroundImage: {
      position: 'bottomRight',
      source: {
        mobile: mobileBannerImage,
        tablet: tabletBannerImage,
        desktop: desktopBannerImage,
      },
    },
  },
  noDataMessage: {
    title: "You don't have any active claims.",
    description:
      "Can't see a claim you recently submitted? Please check again in an hour - your claim details will pop up here then. Need help with your claim? Call us on&nbsp;<a href='tel:1300 467 951'>1300 467 951</a>.",
  },
  needToMakeClaim: {
    header: {
      id: 'need-to-make-claim',
      title: 'Need to make a claim?',
      description: 'Follow these simple steps to make sure your claim is processed quickly.',
    },
    data: [
      {
        id: 'what-happened',
        icon: MessageIcon,
        title: '1. Tell us what happened',
        description:
          'Describe the incident in detail, where and when it happened - and who was affected.',
      },
      {
        id: 'list-expenses',
        icon: ExpenseIcon,
        title: '2. List your expenses',
        description:
          "Provide evidence of the expenses you're claiming eg bills, receipts, bank statements.",
      },
      {
        id: 'upload-document',
        icon: UploadDocIcon,
        title: '3. Upload documents',
        description:
          "See section below for a guide to the 'Documents you need' to support your claim. ",
      },
      {
        id: 'review-submit',
        icon: ReviewIcon,
        title: '4. Review and submit',
        description: 'Review your claim details, save and continue later, or submit.',
      },
    ],
    link: {
      id: 'find-policy-link',
      href: ROUTE_PATHS[ROUTES.POLICIES],
      label: 'Find your policy and make a claim',
    },
  },
  documentsYouNeed: {
    header: {
      id: 'documents-you-need',
      title: "Documents you'll need to make a claim",
      description:
        "You'll likely need to gather a few documents before you make your claim. This information helps us understand what happened and how we can help.<br /><br /><strong>Good to know:</strong> Your claim assessment may be quicker if you collect and submit the right documents from the get-go.<br /><br /><strong>What type of claim are you making?</strong>",
      subTitle:
        "Select the topic most relevant to your experience and we'll show you the documents you'll need to support your claim.",
    },
    data: [
      {
        id: 'documents-travel-delay',
        icon: TravelIcon,
        title: 'Travel disruption',
        items: [
          `<p><strong>Travel delay</strong></p>
          <ul>
            <li><strong>All invoices and receipts</strong> for the expenses you're claiming</li>
            <li><strong>A letter from the transport provider</strong> confirming the reason for the delay, how long it lasted, and any compensation offered including refunds on original itinerary</li>
          </ul>
          <p><strong>Medical condition</strong></p>
          <ul>
            <li><strong>All invoices and receipts</strong> for the expenses you're claiming</li>
            <li><strong>Medical reports</strong> from the overseas provider confirming your diagnosis</li>
            <li><strong>Medical form</strong> (completed by usual medical practitioner)</li>
          </ul>
          <p><strong>Flights</strong></p>
          <ul>
            <li><strong>All invoices and receipts</strong> for the expenses you're claiming</li>
            <li>Original flight itinerary</li>
            <li>Confirmation of cancellation and refunds/credits applicable</li>
          </ul>
          <p><strong>Land and Cruise Bookings</strong></p>
          <ul>
            <li><strong>All invoices and receipts</strong> for the expenses you're claiming</li>
            <li>Original booking details/itinerary</li>
            <li>Confirmation of cancellation and refunds/credits applicable</li>
          </ul>
          `,
        ],
      },
      {
        id: 'documents-medical-event',
        icon: MedicIcon,
        title: 'Medical event',
        items: [
          `<p><strong>Note:</strong> Medical related expenses can only be claimed under international policies for costs incurred, and events arising, outside of Australia or overseas. Please refer to your Product Disclosure Statement (or PDS) to learn more.</p>
          <ul>
            <li><strong>All invoices and receipts</strong> for the expenses you're claiming</li>
            <li><strong>Medical reports</strong> from the overseas provider confirming your diagnosis</li>
            <li><strong>Medical form</strong> (completed by usual medical practitioner)</li>
            <li><strong>Dental claims:</strong> confirmation from the treating dentist that the treatment was not due to decay or deterioration</li>
          </ul>`,
        ],
      },
      {
        id: 'documents-luggage',
        icon: LuggageIcon,
        title: 'Luggage',
        items: [
          `<p><strong>Delayed luggage</strong></p>
            <ul>
              <li>
                <strong>Loss or delay report from the transport provider</strong>, confirming:
                <ul>
                  <li>that your luggage was delayed</li>
                  <li>how long it was delayed</li>
                  <li>any compensation paid while your luggage was delayed</li>
                </ul>
              </li>
              <li>
                <strong>All invoices and receipts </strong>for the essential emergency purchases made while your luggage was delayed, such as:
                <ul>
                  <li>clothing</li>
                  <li>toiletries</li>
                  <li>shoes</li>
                  <li>non-prescription medication</li>
                  <li>a basic bag</li>
                </ul>
              </li>
            </ul>
            <p><strong>Lost / Damaged / Stolen luggage</strong></p>
            <ul>
              <li>
                <strong>Loss, stolen or damage report</strong> from a responsible authority (for example, police, hotel, security, or transport authority) confirming the loss occurred
              </li>
              <li>
                <strong>Proof of ownership</strong> for each item, which could include:
                <ul>
                  <li>Purchase receipt, invoice, or bank statement showing the item, purchase date, and amount paid</li>
                  <li>In some cases, valuation certificates (issued before the loss), ATM receipts, or warranty cards with a matching bank statement</li>
                </ul>
              </li>
            </ul>
          `,
        ],
      },
      {
        id: 'documents-rental',
        icon: CarIcon,
        title: 'Rental vehicle',
        items: [
          `<ul>
            <li><strong>Rental agreement or contract</strong> showing the excess you were liable to pay</li>
            <li><strong>Itemised repair invoice</strong> showing the cost of vehicle repairs</li>
            <li><strong>Proof of charges</strong> showing the amount debited by the rental car company for the damage or excess</li>
            <li><strong>Police or authority report</strong> relating to the incident</li>
            <li><strong>If another party was at fault,</strong> written confirmation of the compensation payable by them or their insurer</li>
          </ul>`,
        ],
      },
      {
        id: 'documents-other-event',
        icon: OtherEventIcon,
        title: { default: 'Any other event', short: 'Other event' },
        items: [
          `<ul>
            <li><strong>All invoices and receipts</strong> for the expenses you're claiming</li>
          </ul>`,
        ],
      },
    ],
  },
};

export default claimsData;
